import { TextField, Stack, SxProps, Theme, FormControl, InputLabel, OutlinedInput, InputAdornment, IconButton } from '@mui/material';
import { CPFormHelperText } from './CPFormHelperText';
import { capitalize } from '@warden-ui/common';
import { useState } from 'react';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface CPTextFieldProps {
  id: string;
  label: string;
  value: string;
  type?: string;
  autoFocus?: boolean;
  errorMessage?: string;
  capitalizeFirstLetterOnly?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  onChange?: (val: string) => void;
  inputProps?: any;
  sx?: SxProps<Theme>;
}

export const CPTextField = ({
  id,
  label,
  value,
  type = 'text',
  autoFocus = true,
  capitalizeFirstLetterOnly = false,
  variant = 'outlined',
  errorMessage = '',
  onChange = () => {},
  // by default, disable autocomplete (https://stackoverflow.com/a/56224685)
  inputProps = { autocomplete: 'off' },
  ...props
}: CPTextFieldProps) => {
  const hasErrorMessage = errorMessage.length > 0;
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const passwordInputProps = type === 'password'
    ? { ...inputProps, autocomplete: 'current-password' }
    : inputProps;

  return (
    <Stack spacing={0.5} sx={props.sx} className='w-100'>
      {type === 'password' ? (
        <FormControl variant={variant} error={hasErrorMessage} fullWidth>
          <InputLabel htmlFor={id}>{capitalize(label, capitalizeFirstLetterOnly)}</InputLabel>
          <OutlinedInput
            id={id}
            type={showPassword ? 'text' : 'password'}
            value={value}
            autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? 'hide the password' : 'display the password'
                  }
                  onClick={handleClickShowPassword}
                  edge="end"
                  tabIndex={-1}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            label={capitalize(label, capitalizeFirstLetterOnly)}
            inputProps={passwordInputProps}
          />
        </FormControl>
      ) : (
        <TextField
          id={id}
          label={capitalize(label, capitalizeFirstLetterOnly)}
          variant={variant}
          value={value}
          type={type}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          aria-describedby={`${id}-helper`}
          color={hasErrorMessage ? 'error' : 'primary'}
          error={hasErrorMessage}
          InputProps={inputProps}
          fullWidth
        />
      )}

      <CPFormHelperText
        id={`${id}-helper`}
        message={errorMessage}
        error={hasErrorMessage}
      />
    </Stack>
  );
};